/**
 * §F Competitive Set — AI Suggest request/response mapper.
 *
 * Consumes `@/services/brandBibleAiSuggest` (structured A–I contract). Does
 * not call the legacy `{prompt, kind}` body (422). One button covers all four
 * editable fields; name and domain are refuse-only and are never shown as
 * drafts even if the model returns them.
 */
import type {
  AISuggestRequest,
  AISuggestResponse,
  ContextItem,
  FieldDraft,
  FieldRefusal,
  TargetFieldMapping,
} from '@/services/brandBibleAiSuggest';
import { isValidUnverifiedDraft } from '@/services/brandBibleAiSuggest';

export const SECTION_F_CODE = 'F' as const;

export type CompetitorAIField = 'name' | 'domain' | 'position_note' | 'our_angle';

/** Stable order the one action always sends. Dropping any field is a coverage bug. */
export const COMPETITOR_AI_FIELDS: readonly CompetitorAIField[] = [
  'name',
  'domain',
  'position_note',
  'our_angle',
];

export const COMPETITOR_FIELD_COVERAGE: Record<
  CompetitorAIField,
  { label: string; defaultRefusal: string }
> = {
  name: {
    label: 'Name',
    defaultRefusal: 'No suggestion — competitor names and domains are entered by you, never generated.',
  },
  domain: {
    label: 'Domain',
    defaultRefusal: 'No suggestion — competitor names and domains are entered by you, never generated.',
  },
  position_note: {
    label: 'Position note',
    defaultRefusal: 'No suggestion — add what you know first; this only rewrites your own wording.',
  },
  our_angle: {
    label: 'Our angle',
    defaultRefusal: 'No suggestion — add your own strengths to your Brand Bible first; this is built only from them.',
  },
};

export const PROVIDER_ERROR_MSG =
  "Suggestions couldn't be generated — the AI service didn't respond. Nothing was changed; try again.";
export const OUTPUT_ERROR_MSG =
  "The AI returned a response that didn't fit the expected shape, so it was discarded — nothing was written. Try again.";
export const MISSING_TENANT_ERROR = 'tenant_id is required for Brand Bible AI suggestions';

const CANNED_SOURCE_RE = /guided defaults|demo data|reshaped from your own data|backend pending/i;
const CONTEXT_MAX = 24;
const CONTEXT_VALUE_MAX = 1500;
const EXISTING_VALUE_MAX = 400;
const EXISTING_MAX = 20;
const INSTRUCTION_MAX = 400;
const PROMPT_MAX = 2000;

export type CompetitorSuggestBrand = {
  businessName?: string;
  description?: string;
  voice?: string;
  offers?: string;
  differentiators?: string;
};

export type CompetitorSuggestRecord = {
  id: string;
  name: string;
  domain?: string;
  position_note?: string;
  our_angle?: string;
};

/** Per-record dismissal key so one competitor's dismissals cannot silence another. */
export function competitorNotesSectionId(recordId: string): string {
  return `competitors:notes:${recordId}`;
}

export function hasBrandGrounding(brand: CompetitorSuggestBrand): boolean {
  return !!(
    brand.businessName?.trim() ||
    brand.description?.trim() ||
    brand.differentiators?.trim() ||
    brand.offers?.trim() ||
    brand.voice?.trim()
  );
}

function clip(value: string, max: number): string {
  const trimmed = value.trim();
  return trimmed.length <= max ? trimmed : trimmed.slice(0, max);
}

function boundExisting(values: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  for (const raw of values) {
    if (typeof raw !== 'string') continue;
    const next = clip(raw, EXISTING_VALUE_MAX);
    if (!next) continue;
    out.push(next);
    if (out.length >= EXISTING_MAX) break;
  }
  return out;
}

function pushContext(out: ContextItem[], key: string, raw: string | undefined): void {
  const value = clip(raw ?? '', CONTEXT_VALUE_MAX);
  if (!value) return;
  if (out.some((item) => item.key === key)) return;
  if (out.length >= CONTEXT_MAX) return;
  out.push({ key, value });
}

export function buildCompetitorTenantContext(brand: CompetitorSuggestBrand): ContextItem[] {
  const tenant_context: ContextItem[] = [];
  pushContext(tenant_context, 'business_name', brand.businessName);
  pushContext(tenant_context, 'description', brand.description);
  pushContext(tenant_context, 'differentiators', brand.differentiators);
  pushContext(tenant_context, 'offers', brand.offers);
  pushContext(tenant_context, 'voice', brand.voice);
  return tenant_context;
}

export function buildCompetitorSectionContext(record: CompetitorSuggestRecord): ContextItem[] {
  const section_context: ContextItem[] = [];
  pushContext(section_context, 'competitor_name', record.name);
  pushContext(section_context, 'position_note', record.position_note);
  pushContext(section_context, 'our_angle', record.our_angle);
  pushContext(
    section_context,
    'internal_only',
    'Competitor records are internal intelligence. Never name a competitor in public copy.',
  );
  return section_context;
}

function target(partial: TargetFieldMapping): TargetFieldMapping {
  return {
    ...partial,
    existing: boundExisting(partial.existing),
    dismissed: boundExisting(partial.dismissed),
    instruction: clip(partial.instruction, INSTRUCTION_MAX),
  };
}

export function buildCompetitorTargets(
  record: CompetitorSuggestRecord,
  dismissed: string[],
): TargetFieldMapping[] {
  const dismissedList = boundExisting(dismissed);
  return [
    target({
      output_key: 'name',
      target_field: 'name',
      instruction:
        'Refuse. Never invent, guess, or reshape a competitor name — including from a stored domain. Names are entered by a human only.',
      kind: 'text',
      max_drafts: 1,
      claim_policy: 'grounded',
      existing: [record.name],
      dismissed: dismissedList,
    }),
    target({
      output_key: 'domain',
      target_field: 'domain',
      instruction:
        'Refuse. Never invent, guess, or reshape a competitor domain or URL — including from a stored name. Domains are entered by a human only.',
      kind: 'text',
      max_drafts: 1,
      claim_policy: 'grounded',
      existing: record.domain ? [record.domain] : [],
      dismissed: dismissedList,
    }),
    target({
      output_key: 'position_note',
      target_field: 'position_note',
      instruction:
        'Rewrite-only the human existing position_note in section_context. Add no rankings, market-share, #1 claims, capabilities, or new facts. Preserve uncertainty. If no existing note, refuse.',
      kind: 'text',
      max_drafts: 1,
      claim_policy: 'grounded',
      existing: record.position_note ? [record.position_note] : [],
      dismissed: dismissedList,
    }),
    target({
      output_key: 'our_angle',
      target_field: 'our_angle',
      instruction:
        'Draft how WE position using ONLY tenant-owned strengths in tenant_context (business_name, description, differentiators, offers, voice). Never assert facts about the competitor. Internal only. If tenant strengths are missing, refuse.',
      kind: 'text',
      max_drafts: 1,
      claim_policy: 'grounded',
      existing: record.our_angle ? [record.our_angle] : [],
      dismissed: dismissedList,
    }),
  ];
}

const F_PROMPT = [
  'Section F competitive-set drafts are INTERNAL-ONLY and never published.',
  'Use only the output_key values supplied. Do not invent competitor names or domains.',
  'Do not generate rankings, market-share, #1 claims, URLs, credentials, or competitor capabilities.',
  'position_note is REWRITE-ONLY of the human existing position_note in section_context. Preserve uncertainty. If none supplied, refuse.',
  'our_angle must draw ONLY on tenant_context strengths — a claim about US, never about the competitor. If too thin, refuse.',
  'Competitor intelligence must not leak into public-facing copy or be stated as verified fact.',
  'Never mark a value accepted, approved, public, or verified.',
].join(' ');

export function buildCompetitorSuggestRequest(args: {
  record: CompetitorSuggestRecord;
  brand: CompetitorSuggestBrand;
  dismissed?: string[];
}): AISuggestRequest {
  const { record, brand, dismissed = [] } = args;
  return {
    section: SECTION_F_CODE,
    section_id: competitorNotesSectionId(record.id),
    prompt: clip(F_PROMPT, PROMPT_MAX),
    targets: buildCompetitorTargets(record, dismissed),
    tenant_context: buildCompetitorTenantContext(brand),
    section_context: buildCompetitorSectionContext(record),
  };
}

export function isCompetitorAIField(value: string): value is CompetitorAIField {
  return (COMPETITOR_AI_FIELDS as readonly string[]).includes(value);
}

export function resolveCompetitorField(draft: Pick<FieldDraft, 'output_key' | 'target_field'>): CompetitorAIField | null {
  if (isCompetitorAIField(draft.target_field)) return draft.target_field;
  if (isCompetitorAIField(draft.output_key)) return draft.output_key;
  return null;
}

export function isCannedSource(source?: string): boolean {
  return typeof source === 'string' && CANNED_SOURCE_RE.test(source);
}

export interface MappedCompetitorSuggest {
  drafts: FieldDraft[];
  refusals: FieldRefusal[];
  source: string;
}

function forcedNameDomainRefusals(): FieldRefusal[] {
  return (['name', 'domain'] as const).map((field) => ({
    output_key: field,
    target_field: field,
    reason: COMPETITOR_FIELD_COVERAGE[field].defaultRefusal,
    unsafe_categories: [],
  }));
}

/** Readiness refusals shown before a fetch: name/domain always; empty note / empty brand. */
export function readinessRefusals(
  record: CompetitorSuggestRecord,
  brand: CompetitorSuggestBrand,
): FieldRefusal[] {
  const refusals = forcedNameDomainRefusals();
  if (!record.position_note?.trim()) {
    refusals.push({
      output_key: 'position_note',
      target_field: 'position_note',
      reason: COMPETITOR_FIELD_COVERAGE.position_note.defaultRefusal,
      unsafe_categories: [],
    });
  }
  if (!hasBrandGrounding(brand)) {
    refusals.push({
      output_key: 'our_angle',
      target_field: 'our_angle',
      reason: COMPETITOR_FIELD_COVERAGE.our_angle.defaultRefusal,
      unsafe_categories: [],
    });
  }
  return refusals;
}

export function mapCompetitorSuggestResponse(response: AISuggestResponse): MappedCompetitorSuggest {
  if (isCannedSource(response.source)) {
    throw new Error('canned source rejected');
  }

  const drafts: FieldDraft[] = [];
  const seen = new Set<string>();
  for (const draft of response.drafts ?? []) {
    const field = resolveCompetitorField(draft);
    if (!field) continue;
    // name/domain are refuse-only — never surface a model (or reshape) draft.
    if (field === 'name' || field === 'domain') continue;
    if (!isValidUnverifiedDraft(draft)) continue;
    const key = `${field}:${draft.value.trim().toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    drafts.push({ ...draft, output_key: field, target_field: field });
  }

  const refusalsByField = new Map<CompetitorAIField, FieldRefusal>();
  for (const refusal of response.refusals ?? []) {
    const field = resolveCompetitorField(refusal);
    if (!field) continue;
    if (!refusalsByField.has(field)) refusalsByField.set(field, { ...refusal, output_key: field, target_field: field });
  }
  for (const forced of forcedNameDomainRefusals()) {
    if (!refusalsByField.has(forced.target_field as CompetitorAIField)) {
      refusalsByField.set(forced.target_field as CompetitorAIField, forced);
    }
  }

  const drafted = new Set(drafts.map((d) => d.target_field));
  const refusals = COMPETITOR_AI_FIELDS.filter((f) => !drafted.has(f)).map(
    (field) =>
      refusalsByField.get(field) ?? {
        output_key: field,
        target_field: field,
        reason: COMPETITOR_FIELD_COVERAGE[field].defaultRefusal,
        unsafe_categories: [],
      },
  );

  return {
    drafts,
    refusals,
    source: response.source || 'AI draft · unverified',
  };
}

/** Display card text. Routing uses a Map keyed by this exact string — never parsed. */
export function competitorDraftCard(draft: FieldDraft): string {
  const field = resolveCompetitorField(draft);
  const label = field ? COMPETITOR_FIELD_COVERAGE[field].label : draft.target_field;
  return `[${label}] ${draft.value}`;
}

export function classifySuggestError(err: unknown): string {
  const axiosDetail = (err as { response?: { data?: { detail?: unknown }; status?: number } })?.response;
  const detail = String(axiosDetail?.data?.detail ?? (err instanceof Error ? err.message : String(err)));
  if (/tenant_id is required/i.test(detail)) return MISSING_TENANT_ERROR;
  if (axiosDetail?.status === 422 || /unmapped|invalid structured|invalid or unmapped/i.test(detail)) {
    return OUTPUT_ERROR_MSG;
  }
  return PROVIDER_ERROR_MSG;
}
